<template>
  <div class="role-card">
    <ExBreadcrumbs 
      class="breadcrumbs-area" 
      :breadcrumbsList="computedBreadcrumbs"
      @onClickBreadcrumb="handleBreadcrumbClick"
    />
    <div class="role-header">
      <OrgObjectHeader
        type="Role"
        :status="roleData?.status || status"
        :code="roleData ? getOrgCodeName(roleData) : code"
        :name="roleData?.name || name"
        :archived="roleData?.archived ?? archived"
        :currentPosition="roleData"
        :dataStream="roleChatterData"
        :totalChatterCount="totalRoleChatterData"
        :chatterDrawerOpen="chatterDrawerOpen"
        :showAttachToPositionsBulk="true"
        @update:chatterDrawerOpen="chatterDrawerOpen = $event"
        @reloadChatterList="reloadChatterData"
        @requestMoreCheckpointsChatter="getChatterData"
        @optionClick="handleOptionClick"  
        :attachedToPositionsCount="roleData?.active_attached_position_count ?? 0"
        :attachedToResponsibilitiesCount="roleData?.active_attached_responsibilities_count ?? 0"
        :inactivePositionsCount="roleData?.inactive_attached_position_count ?? 0"
        :inactiveResponsibilitiesCount="roleData?.inactive_attached_responsibilities_count ?? 0"

      />
    </div>
    <RoleCardMenu
      :value="selectedTab"
      :active_responsibilities_count="roleData?.active_attached_responsibilities_count ?? 0"
      :current_assignments_count="roleData?.assignment_count ?? 0"
      :attached_to_positions_count="roleData?.active_attached_position_count ?? 0"
      :has_purpose="showContextTabV2 ? !!roleData?.context : !!roleData?.purpose"
      :show_context_count_badge="showContextCountBadge"
      :context_files_count="contextFilesCount"
      :has_context="hasContext"
      @tab-change="onTabChange"
    />
    <div class="role-card-main-content">
      <!-- ER-11574: whole-page skeleton while the role detail fetch is in flight. -->
      <v-skeleton-loader
        v-if="skeletonLoading"
        type="heading, button, article"
        class="role-skeleton-loader"
      />
      <v-tabs-items v-else v-model="selectedTab" class="custom-tabs">
        <v-tab-item :transition="false" :reverse-transition="false">
          <v-card elevation="0">
            <v-card-text>
              <RoleResponsibilitiesTab 
                ref="responsibilitiesTabRef" 
                :roleId="computedRoleId" 
                :drilldownInPanel="effectiveDrilldownInPanel"
                :allow-drag-responsibility-to-tree="allowDragResponsibilityToTree"
                :enable-responsibilities-reorder="enableResponsibilitiesReorder"
                :on-responsibility-drag-start-callback="onResponsibilityDragStartCallback"
                :on-responsibility-drag-end-callback="onResponsibilityDragEndCallback"
                @roleDataUpdated="handleRoleDataUpdated"
                @role-attached-responsibilities-sync="
                  (p) => emit('role-attached-responsibilities-sync', p)
                "
                @pane-after-responsibility-attached="
                  (p) => emit('pane-after-responsibility-attached', p)
                "
                @drilldown="(data) => emit('drilldown', data)"
              />
            </v-card-text>
          </v-card>
        </v-tab-item>

        <v-tab-item :transition="false" :reverse-transition="false">
          <v-card elevation="0">
            <v-card-text>
              <AttachedToPositionsTab
                ref="attachedToPositionsTabRef"
                :roleId="computedRoleId"
                :roleName="roleData?.name || name"
                :perPage="10"
                :initialFilters="['draft', 'active']"
                @detach="handleDetachPosition"
                @menuAction="handleMenuAction"
                @roleDataUpdated="handleRoleDataUpdated"
                @openChatter="handleOpenChatter"
                @drilldown="handleDrilldown"
                :hideAttachButton="true"
                class="attached-to-positions-tab-container"
                />
            </v-card-text>
          </v-card>
        </v-tab-item>

        <v-tab-item :transition="false" :reverse-transition="false">
          <v-card elevation="0">
            <v-card-text>
              <!-- Assignments Tab Content -->
            </v-card-text>
          </v-card>
        </v-tab-item>

        <v-tab-item :transition="false" :reverse-transition="false">
          <v-card elevation="0">
            <v-card-text class="custom-view purpose-view">
              <!--
                ER-9892 / MDP-494: behind the idea-MDP-494 flag, render the new ContextTab;
                otherwise keep the legacy RolePurposeTab.
              -->
              <ContextTab
                v-if="showContextTabV2"
                :state="contextState"
                :context="roleData?.context || null"
                :context-updated-at="roleData?.context_updated_at || null"
                :context-updated-by="roleData?.context_updated_by || null"
                :files="contextFiles"
                :uploading-files="uploadingContextFiles"
                :disabled="isSavingContext"
                :draft="contextDraft"
                :max-length="ENTITY_CONTEXT_MAX_LENGTH"
                :suggesting="isSuggestingContext"
                :suggest-error="contextSuggestError"
                @save="onContextSave"
                @suggest="onContextSuggest"
                @improve="onContextImprove"
                @input="setContextDraft"
                @discard="clearContextDraft"
                @upload="onContextUpload"
                @remove="onContextRemove"
              />
              <RolePurposeTab
                v-else
                :value="roleData?.purpose || purpose"
                placeholder="Enter purpose for this role..."
                :maxLength="500"
                :isSaving="isSavingPurpose"
                @save="onPurposeSave"
                :hideAIButtons="true"
              />
            </v-card-text>
          </v-card>
        </v-tab-item>
      </v-tabs-items>
    </div>
    
    <ActivateRoleDialog
      v-if="activateRoleDialog && roleData"
      v-model="activateRoleDialog"
      :name="roleData?.name || ''"
      :employee-count="activationEmployeeCount"
      @onSave="onActivateRoleConfirm"
    />
    <ActivateResponsibilityConfirmationModal
      :dialog="showActivateResponsibility"
      @close="showActivateResponsibility = false"
      @skip="skipActivateResponsibility"
      @activate="activateAttachedResponsibilities"
      :role="roleData?.name || ''"
      :draftResponsibilityCount="draftResponsibilityCount"
      :skipButtonLoader="skipActivateButtonLoader"
      :activateButtonLoader="activateAttachedButtonLoader"
    />
    <RenameRoleDialog
      v-if="showRenameRoleDialog && roleData"
      v-model="showRenameRoleDialog"
      :role="roleData"
      @saved="onRoleRenamed"
    />
    <DeleteRoleDialog
      v-if="deleteRoleDialog && roleData"
      :show="deleteRoleDialog"
      :name="roleData?.name || ''"
      @onDelete="deleteOrgRole"
      @onClose="deleteRoleDialog = false"
    />
    <RetireRoleDialog
      v-if="retiredRoleDialog && roleData"
      :show="retiredRoleDialog"
      :name="roleData?.name || ''"
      :role="roleData"
      @onRetire="retiredOrgRole"
      @onClose="retiredRoleDialog = false"
    />
    <DeActivateRole
      v-if="roleData"
      v-model="deActivateRoleDialog"
      @onSave="deActivateOrgRole"
      :role="roleData"
    />
    <ReactivateRoleDialog
      v-if="reActivateRoleDialog && roleData"
      :show="reActivateRoleDialog"
      :name="roleData?.name || ''"
      :role="roleData"
      :employee-count="activationEmployeeCount"
      @onReactivate="reActivateOrgRole"
      @onClose="reActivateRoleDialog = false"
    />
    <ArchivePopUp
      :action="archiveAction"
      type="role"
      :show="showArchiveDialog"
      :curretObj="roleData"
      :errorMessage="unarchiveError"
      @onClose="onArchiveDialogClose"
      @onArchive="onArchiveCatalogs"
    />
    <UnarchiveNameConflictDialog
      :show="unarchiveConflictOpen"
      objectType="role"
      :currentName="unarchiveTarget?.name || ''"
      :objectId="unarchiveTarget?.id || ''"
      :accountId="accountId"
      :busy="unarchiveBusy"
      :errorMessage="unarchiveError"
      :nameError="unarchiveNameError"
      @confirm="unarchiveRole.submitRename"
      @close="onArchiveDialogClose"
      @clear-error="unarchiveRole.clearErrors"
    />
    <AttachedResponsibility
      v-if="attachResponsibilityDialog"
      v-model="attachResponsibilityDialog"
      @onSave="handleAttachResponsibility"
      :items="attachableResponsibilities"
      @item-selected="handleResponsibilitySelected"
      @add-item-selected="handleAddNewResponsibility"
      :parent-name="roleData?.name || name"
      :openDefault="openDefault"
      :type="'attach_responsibility_role_new_picker'"
      @load-more="loadMoreResponsibilities"
      @search="searchResponsibilities"
      :hasMoreData="hasMoreResponsibilitiesData"
      :isLoadingMore="isLoadingMoreResponsibilities"
      :isAssigning="isAssigningResponsibility"
      :attach-target="attachTargetRole"
      @created-and-attached="handleResponsibilityCreatedAndAttached"
    />
    <AttachRoleToPositionsInBulkDialog
      v-model="showAttachRoleToPositionsBulkDialog"
      :role-name="roleData?.name || name"
      action-type="attach"
      :positions="bulkAttachPositions"
      :loading="isBulkAttachPositionsLoading"
      :total="bulkAttachTotalPositions"
      :per-page="10"
      @onRequest="handleBulkAttachPositionRequest"
      @onSubmit="handleBulkAttachRoleToPositions"
      @onAddPosition="onBulkAttachAddPosition"
      @close="closeAttachRoleToPositionsBulkDialog"
    />
    <ContextIQOverlay
      v-if="contextIQOverlayEnabled"
      :model-value="isContextIQOverlayOpen"
      object-type="role"
      :object-id="roleData?.id"
      :object-name="roleData?.name || name"
      :context="contextOverlaySeed"
      @save="onContextOverlaySave"
      @close="isContextIQOverlayOpen = false"
    />
  </div>
</template>

<script setup>
import { defineProps, ref, computed, onMounted, getCurrentInstance } from 'vue';
import { useRouter } from 'vue-router/composables';
import launchDarklyService from '@/services/launchdarkly';
import { LD_FLAG_IDEA_MDP_494, LD_FLAG_IDEA_MDP_523, LD_FLAG_IDEA_MDP_560 } from '@components/constants/launchDarklyFlagKeys';
import ExBreadcrumbs from '@components/breadcrumbs/ExBreadcrumbs.vue';
import OrgObjectHeader from '@components/OrgObjectHeader/OrgObjectHeader.vue';
import RoleCardMenu from '@components/RoleCardMenu/RoleCardMenu.vue';
import RolePurposeTab from '@components/RolePurposeTab/RolePurposeTab.vue';
import ContextTab from '@components/ContextTab/ContextTab.vue';
import ContextIQOverlay from '@components/ContextIQOverlay/ContextIQOverlay.vue';
import {
  useContextSuggestion,
  ENTITY_CONTEXT_MAX_LENGTH,
} from '@components/utils/useContextSuggestion';
import RoleResponsibilitiesTab from '@components/RoleResponsibilitiesTab/RoleResponsibilitiesTab.vue';
import AttachedToPositionsTab from '@components/AttachedToPositionsTab/AttachedToPositionsTab.vue';
import ActivateRoleDialog from '@components/ActivateRoleDialog/ActivateRoleDialog.vue';
import ActivateResponsibilityConfirmationModal from '@components/ActivateResponsibilityConfirmationModal/ActivateResponsibilityConfirmationModal.vue';
import RetireRoleDialog from '@components/RetireRoleDialog/RetireRoleDialog.vue';
import DeleteRoleDialog from '@components/DeleteRoleDialog/DeleteRoleDialog.vue';
import RenameRoleDialog from '@components/RenameRoleDialog/RenameRoleDialog.vue';
import DeActivateRole from '@components/RolesCatalog/DeActivateRole.vue';
import ReactivateRoleDialog from '@components/ReactivateRoleDialog/ReactivateRoleDialog.vue';
import ArchivePopUp from '@components/common/ArchivePopUp.vue';
import UnarchiveNameConflictDialog from '@components/UnarchiveNameConflictDialog/UnarchiveNameConflictDialog.vue';
import { useUnarchiveOrgObject } from '@/composables/useUnarchiveOrgObject';
import AttachedResponsibility from '@components/RolesCatalog/AttachedRole.vue';
import AttachRoleToPositionsInBulkDialog from '@components/AttachRoleToPositionsInBulkDialog/AttachRoleToPositionsInBulkDialog.vue';
import {
  getRole,
  updateRole,
  updateActiveRole,
  editRole,
  updateRetiredRole,
  updateDeActiveRole,
  updateReActiveRole,
  deleteReadinessRole,
  deleteReadinessRoleWithResponsibilities,
  updateArchiveRole,
  attachResponsibilityToRole,
  fetchAttachableResponsibilities,
  getChatterList,
  getListOfPositionsV2,
  getListOfPositionsAttachedToRole,
  attachRoleToPosition,
  getContextFiles,
  uploadContextFiles,
  deleteContextFile,
  mapContextFileToUi,
} from '@/api';
import { fetchRoleActivationCount } from '@/utils/activationEmployeeCount';

const props = defineProps({
  breadcrumbsList: {
    type: Array,
    default: () => []
  },
  status: {
    type: String,
    default: ''
  },
  code: {
    type: String,
    default: ''
  },
  name: {
    type: String,
    default: ''
  },
  archived: {
    type: Boolean,
    default: false
  },
  currentAssignmentsCount: {
    type: Number,
    default: 0
  },
  attachedToPositionsCount: {
    type: Number,
    default: 0
  },
  purpose: {
    type: String,
    default: ''
  },
  responsibilities: {
    type: Array,
    default: () => []
  },
  roleId: {
    type: [Number, String],
    default: null
  },
  /** When true (e.g. Positions Tree View), drilldown and breadcrumb stay in same panel */
  drilldownInPanel: {
    type: Boolean,
    default: false
  },
  /** Pane tree: drag responsibility rows onto another role in the tree to attach */
  allowDragResponsibilityToTree: {
    type: Boolean,
    default: false,
  },
  onResponsibilityDragStartCallback: {
    type: Function,
    default: null,
  },
  onResponsibilityDragEndCallback: {
    type: Function,
    default: null,
  },
});

const instance = getCurrentInstance();
const proxy = instance?.proxy;
const route = proxy?.$route;
const router = useRouter();

// Use in-panel drilldown when parent passes it, or when URL indicates tree view (positions tab with positionId/roleId in query)
/** Pane: tree DnD flag off disables reorder + tree attach; standalone role page keeps reorder. */
const enableResponsibilitiesReorder = computed(
  () => !props.drilldownInPanel || props.allowDragResponsibilityToTree,
);

const effectiveDrilldownInPanel = computed(() => {
  if (props.drilldownInPanel) return true;
  const hasTreeQuery = route?.query?.positionId != null || route?.query?.roleId != null;
  const isPositionsContext =
    (route?.name === 'NewOrganization' && route?.params?.tab === 'positions') ||
    (route?.path?.includes('/positions') && hasTreeQuery);
  return Boolean(hasTreeQuery && isPositionsContext);
});

const emit = defineEmits([
  'backToList',
  'drilldown',
  'roleDataUpdated',
  'role-attached-responsibilities-sync',
  'pane-after-responsibility-attached',
  'item-deleted',
]);

const responsibilitiesTabRef = ref(null)
const attachedToPositionsTabRef = ref(null)
const selectedTab = ref(0);
/** True while the role detail fetch is in flight (drives the in-content skeleton). */
const isLoading = ref(false);
/** idea-MDP-560 rollout gate: show the whole-page skeleton instead of the legacy spinner. */
const skeletonLoadingEnabled = ref(false);
const skeletonLoading = computed(() => isLoading.value && skeletonLoadingEnabled.value);
const roleData = ref(null);
const accountId = ref(null);
const roleId = ref(null);
const roleChatterData = ref([]);
const totalRoleChatterData = ref(0);
const loadedPages = ref({});
const chatterDrawerOpen = ref(false);

const activateRoleDialog = ref(false);
const activationEmployeeCount = ref(0);
const showActivateResponsibility = ref(false);
const skipActivateButtonLoader = ref(false);
const activateAttachedButtonLoader = ref(false);
const retiredRoleDialog = ref(false);
const deleteRoleDialog = ref(false);
const showRenameRoleDialog = ref(false);
const deActivateRoleDialog = ref(false);
const reActivateRoleDialog = ref(false);
const showArchiveDialog = ref(false);
const archiveAction = ref(null);
const attachResponsibilityDialog = ref(false);

const selectedResponsibilityId = ref(null);
const attachableResponsibilities = ref([]);
const openDefault = ref(false);
const hasMoreResponsibilitiesData = ref(false);
const isLoadingMoreResponsibilities = ref(false);
const isAssigningResponsibility = ref(false);
const responsibilitiesPage = ref(1);
const responsibilitiesSearchTerm = ref('');
const responsibilitiesLimit = 10;

const isSavingPurpose = ref(false);
/** idea-MDP-494: render the new ContextTab instead of the legacy purpose tab. Starts false so the old UI shows until LD resolves. */
const showContextTabV2 = ref(false);
/** idea-MDP-523: Context tab numeric count badge (files + context text). Starts false until LD resolves. */
const showContextCountBadge = ref(false);
/** idea-MDP-523 (ER-10140): Suggest / Improve open the IQ Overlay instead of the inline draft. */
const contextIQOverlayEnabled = ref(false);
const isContextIQOverlayOpen = ref(false);
/** idea-MDP-494 ContextTab save-in-flight flag. */
const isSavingContext = ref(false);
/** ER-10218: IQ "Suggest" for the Context field — the suggestion is an unsaved draft. */
const {
  draft: contextDraft,
  isSuggesting: isSuggestingContext,
  suggestError: contextSuggestError,
  suggest: suggestContextText,
  setDraft: setContextDraft,
  clearDraft: clearContextDraft,
} = useContextSuggestion('role');
/** Uploaded context files for this role (ER-10294 context-files API). */
const contextFiles = ref([]);
/** Files mid-upload, rendered as progress-bar cards until the list refresh replaces them. */
const uploadingContextFiles = ref([]);
/** ContextTab visual state: "filled" once a context has been saved, otherwise "empty". */
const contextState = computed(() =>
  (roleData.value?.context || '').trim() ? 'filled' : 'empty'
);

/** The text the IQ Overlay opens on: what the editor currently shows (unsaved draft, else saved). */
const contextOverlaySeed = computed(() => contextDraft.value ?? roleData.value?.context ?? '');

/** Non-empty context (V2) or purpose (legacy) — drives the Context tab count badge. */
const hasContext = computed(() => {
  if (showContextTabV2.value) {
    const context = roleData.value?.context;
    if (typeof context === 'string') {
      return context.trim().length > 0;
    }
    return Boolean(context);
  }
  const purpose = roleData.value?.purpose;
  if (typeof purpose === 'string') {
    return purpose.trim().length > 0;
  }
  return Boolean(purpose);
});

/** Attached context files count for the Context tab badge. */
const contextFilesCount = computed(() => contextFiles.value?.length ?? 0);

const showAttachRoleToPositionsBulkDialog = ref(false);
const bulkAttachPositions = ref([]);
const isBulkAttachPositionsLoading = ref(false);
const bulkAttachTotalPositions = ref(0);
const bulkAttachQuery = ref({
  page: 1,
  perPage: 10,
  search: '',
  statuses: [],
});
const bulkAttachedPositionIds = ref(new Set());

function onTabChange(tabIndex) {
  selectedTab.value = tabIndex;
  // Embedded in a position pane: do not write roleTab to the URL. That query
  // change remounts PositionForm and kicks the user out of pane view.
  if (effectiveDrilldownInPanel.value) return;
  if (proxy.$router && route) {
    const roleTabMap = ['attached_responsibilities', 'attached_to_positions', 'assignments', 'purpose'];
    const roleTabValue = roleTabMap[tabIndex] || 'attached_responsibilities';
    
    proxy.$router.replace({
      ...route,
      query: {
        ...route.query,
        roleTab: roleTabValue
      }
    }).catch(() => {
    });
  }
}

function handleBreadcrumbClick(breadcrumb) {
  if (effectiveDrilldownInPanel.value) {
    if (breadcrumb.positionId && !breadcrumb.disabled) {
      emit('drilldown', { positionId: breadcrumb.positionId });
      return;
    }
    if (breadcrumb.text === 'Roles' || breadcrumb.text === 'Positions') {
      return;
    }
  }
  if (breadcrumb.text === 'Positions') {
    if (proxy?.$router) {
      proxy.$router.push('/organization/positions').catch(() => {});
    }
  } else if (breadcrumb.positionId && !breadcrumb.disabled) {
    if (proxy?.$router) {
      proxy.$router.push({
        name: 'PositionDetailChild',
        params: { positionId: breadcrumb.positionId },
        query: { positionTab: 'attached-roles' }
      }).catch(() => {});
    }
  } else if (breadcrumb.text === 'Roles') {
    if (proxy?.$router) {
      proxy.$router.push('/organization/roles').catch(() => {});
    }
  }
}

const computedRoleId = computed(() => {
  return props.roleId || route?.params?.roleId || route?.query?.roleId || roleData.value?.id || null;
});

const attachTargetRole = computed(() => {
  const role = roleData.value;
  const id = roleId.value;
  const name = String(role?.name || props.name || '').trim();
  const code = String(role?.code_str || role?.code || props.code || '').trim().replace(/^#+/, '').split(/\s+/)[0];
  return id && name && code ? { type: 'role', id, code, name } : null;
});

const draftResponsibilityCount = computed(() => {
  const role = roleData.value;
  if (!role) return 0;
  const attachments = role.attachments || [];
  const draftFromAttachments = attachments.filter(
    (item) => (item.status || item.data?.status) === 'draft'
  ).length;
  const draftFromInfo =
    role.info?.draft_responsibilities_count ?? role.draft_responsibilities_count ?? 0;
  return draftFromAttachments > 0 ? draftFromAttachments : Number(draftFromInfo) || 0;
});

const computedBreadcrumbs = computed(() => {
  if (props.breadcrumbsList && props.breadcrumbsList.length > 0) {
    return props.breadcrumbsList;
  }
  
  return [
    {
      text: "Roles",
      disabled: false,
      href: "/organization/roles",
    },
    {
      text: "Role",
      disabled: true,
    },
  ];
});

async function onPurposeSave(value) {
  try {
    isSavingPurpose.value = true;
    const idRole = roleData.value?.id;
    if (!idRole) {
      throw new Error('No role ID');
    }
    const payload = {
      purpose: value,
    };
    await editRole(accountId.value, idRole, payload);
    await getRoleById(idRole);
  } catch (error) {
    console.error('Error updating purpose:', error);
  } finally {
    isSavingPurpose.value = false;
  }
}

async function onContextSave(value) {
  try {
    isSavingContext.value = true;
    const idRole = roleData.value?.id;
    if (!idRole) {
      throw new Error('No role ID');
    }
    await editRole(accountId.value, idRole, { context: value });
    await getRoleById(idRole);
    clearContextDraft();
  } catch (error) {
    console.error('Error updating context:', error);
  } finally {
    isSavingContext.value = false;
  }
}

function onContextSuggest() {
  if (contextIQOverlayEnabled.value) {
    isContextIQOverlayOpen.value = true;
    return undefined;
  }
  return suggestContextText(roleData.value?.id);
}

function onContextImprove() {
  if (contextIQOverlayEnabled.value) {
    isContextIQOverlayOpen.value = true;
  }
}

/** Keep the overlay's text as the editor draft until the save lands, so a failed save loses nothing. */
function onContextOverlaySave(value) {
  setContextDraft(value);
  return onContextSave(value);
}

async function onContextUpload(fileList) {
  const idRole = roleData.value?.id;
  if (!fileList?.length || !idRole) return;

  uploadingContextFiles.value = Array.from(fileList).map((file) => ({
    name: file.name,
    progress: 0,
  }));

  try {
    isSavingContext.value = true;
    await uploadContextFiles('role', idRole, fileList, (event) => {
      if (!event.total) return;
      const progress = Math.round((event.loaded / event.total) * 100);
      uploadingContextFiles.value = uploadingContextFiles.value.map((file) => ({
        ...file,
        progress,
      }));
    });
    await loadContextFiles(idRole);
  } catch (error) {
    console.error('Error uploading context file:', error);
  } finally {
    isSavingContext.value = false;
    uploadingContextFiles.value = [];
  }
}

async function onContextRemove(fileId) {
  const idRole = roleData.value?.id;
  if (!fileId || !idRole) return;

  try {
    isSavingContext.value = true;
    await deleteContextFile(fileId);
    await loadContextFiles(idRole);
  } catch (error) {
    console.error('Error removing context file:', error);
  } finally {
    isSavingContext.value = false;
  }
}

function getOrgCodeName(currentRole) {
  if (currentRole?.code_str) {
    return currentRole.code_str.split(" ")[0];
  } else {
    return "#" + currentRole?.code;
  }
}

async function fetchRoleData() {
  isLoading.value = true;
  try {
    if (accountId.value && roleId.value) {
      const { data } = await getRole(accountId.value, roleId.value);
      roleData.value = data;
      if (showContextTabV2.value) {
        void loadContextFiles(roleId.value);
      }
    }
  } catch (error) {
    console.error('Error fetching role data:', error);
  } finally {
    isLoading.value = false;
  }
}

/** ER-10294: refresh the Context Files list for this role. */
async function loadContextFiles(idRole) {
  if (!showContextTabV2.value) return;
  try {
    const { data } = await getContextFiles('role', idRole);
    const list = Array.isArray(data) ? data : data?.results || [];
    contextFiles.value = list.map(mapContextFileToUi);
  } catch (error) {
    console.error('Error loading context files:', error);
  }
}

async function loadAttachedPositionIdsForRole(rId) {
  const ids = new Set();
  const limit = 100;
  let offset = 0;
  let count = 0;
  do {
    const response = await getListOfPositionsAttachedToRole(
      accountId.value,
      rId,
      limit,
      offset,
      '',
      {}
    );
    const results = response?.data?.results || [];
    count = response?.data?.count || 0;
    results.forEach((item) => ids.add(item.id));
    offset += limit;
  } while (offset < count);
  return ids;
}

async function loadBulkAttachPositionsPage({
  page = bulkAttachQuery.value.page,
  perPage = bulkAttachQuery.value.perPage,
  search = bulkAttachQuery.value.search,
  statuses = bulkAttachQuery.value.statuses,
} = {}) {
  const offset = (page - 1) * perPage;
  const queryParamsObj = {
    limit: perPage,
    offset,
    archived: false,
    ordering: 'name',
    name: search || undefined,
  };
  if (statuses && statuses.length > 0) {
    queryParamsObj.status = statuses;
  }
  const response = await getListOfPositionsV2({
    account_id: accountId.value,
    queryParamsObj,
  });
  const results = response?.data?.results || [];
  const totalCount = response?.data?.count || 0;
  bulkAttachTotalPositions.value = totalCount;
  bulkAttachPositions.value = results.map((position) => ({
    id: position.id,
    code:
      position.code ??
      String(position.code_str || '').replace('#', '').split(' ')[0] ??
      '',
    name: position.name,
    status: position.status || 'draft',
    attached: bulkAttachedPositionIds.value.has(position.id),
    attachmentsCount:
      position.attachments_count?.role ??
      position.roles_count ??
      0,
    rolesCount:
      position.attachments_count?.role ??
      position.roles_count ??
      0,
    responsibilitiesCount:
      position.attachments_count?.responsibility ??
      position.responsibilities_count ??
      0,
  }));
}

async function handleBulkAttachPositionRequest(requestPayload = {}) {
  if (!showAttachRoleToPositionsBulkDialog.value) return;
  bulkAttachQuery.value = {
    ...bulkAttachQuery.value,
    ...requestPayload,
  };
  isBulkAttachPositionsLoading.value = true;
  try {
    await loadBulkAttachPositionsPage(bulkAttachQuery.value);
  } catch (error) {
    console.error('Error loading bulk attach positions page:', error);
  } finally {
    isBulkAttachPositionsLoading.value = false;
  }
}

async function openAttachRoleToPositionsBulkDialog() {
  const rId = roleData.value?.id || computedRoleId.value;
  if (!rId || !accountId.value) return;
  showAttachRoleToPositionsBulkDialog.value = true;
  bulkAttachQuery.value = {
    page: 1,
    perPage: 10,
    search: '',
    statuses: [],
  };
  bulkAttachedPositionIds.value = new Set();
  bulkAttachTotalPositions.value = 0;
  bulkAttachPositions.value = [];
  isBulkAttachPositionsLoading.value = true;
  try {
    loadAttachedPositionIdsForRole(rId)
      .then((ids) => {
        bulkAttachedPositionIds.value = ids;
        bulkAttachPositions.value = bulkAttachPositions.value.map((position) => ({
          ...position,
          attached: ids.has(position.id),
        }));
      })
      .catch((error) => {
        console.error('Error loading attached position IDs:', error);
      });
    await loadBulkAttachPositionsPage(bulkAttachQuery.value);
  } catch (error) {
    console.error('Error opening bulk attach dialog:', error);
  } finally {
    isBulkAttachPositionsLoading.value = false;
  }
}

async function handleBulkAttachRoleToPositions(payload) {
  const positionIds = payload?.positionIds || [];
  const rId = roleData.value?.id || computedRoleId.value;
  if (!rId || positionIds.length === 0) return;
  proxy.$loader?.setAppLoading(true);
  try {
    await Promise.all(
      positionIds.map((positionId) =>
        attachRoleToPosition(accountId.value, positionId, {
          attachment_id: rId,
        })
      )
    );
    proxy.$notifications?.success('Role attached to selected positions Successfully.');
    showAttachRoleToPositionsBulkDialog.value = false;
    await getRoleById(rId);
    await attachedToPositionsTabRef.value?.refresh();
    emit('roleDataUpdated');
  } catch (error) {
    console.error('Error attaching role to positions in bulk:', error);
  } finally {
    proxy.$loader?.setAppLoading(false);
  }
}

function closeAttachRoleToPositionsBulkDialog() {
  showAttachRoleToPositionsBulkDialog.value = false;
  isBulkAttachPositionsLoading.value = false;
  bulkAttachTotalPositions.value = 0;
  bulkAttachedPositionIds.value = new Set();
  bulkAttachPositions.value = [];
}

function onBulkAttachAddPosition() {
  closeAttachRoleToPositionsBulkDialog();
  router.push({ name: 'NewOrganization', params: { tab: 'Positions' } }).catch(() => {});
}

// ER-11818 / Roles Rules 3.4 & 3.6 — before activating/reactivating a Role, ask the
// backend how many employees will be assigned (N). N > 0 opens the existing confirmation
// dialog (now with the count line); N = 0 skips it and proceeds directly. Preview failure
// degrades to the pre-existing dialog flow rather than blocking activation (ER-11822).
async function openActivationConfirmation(kind) {
  const idRole = roleData.value?.id || roleData.value?.role_id;
  const count = await fetchRoleActivationCount(kind, accountId.value, idRole);
  activationEmployeeCount.value = count ?? 0;
  if (count === 0) {
    await (kind === 'activate' ? onActivateRoleConfirm() : reActivateOrgRole());
    return;
  }
  if (kind === 'activate') activateRoleDialog.value = true;
  else reActivateRoleDialog.value = true;
}

async function handleOptionClick(operation) {
  const action = operation.item?.value;
  
  switch (action) {
    case 'activate_action':
      await openActivationConfirmation('activate');
      break;
    case 'retire_action':
      retiredRoleDialog.value = true;
      break;
    case 'reactivate_action':
      await openActivationConfirmation('reactivate');
      break;
    case 'rename_action':
      showRenameRoleDialog.value = true;
      break;
    case 'delete_action':
      deleteRoleDialog.value = true;
      break;
    case 'deactivate_action':
      deActivateRoleDialog.value = true;
      break;
    case 'archive_action':
      archiveAction.value = 'archive';
      showArchiveDialog.value = true;
      break;
    case 'unarchive_action':
      archiveAction.value = 'unarchive';
      unarchiveRole.start({ id: roleData.value?.id, name: roleData.value?.name });
      break;
    case 'attach_responsibility':
      handleOpenAttachResponsibility();
      break;
    case 'attach_role_positions_bulk':
      await openAttachRoleToPositionsBulkDialog();
      break;
    default:
      break;
  }
}

async function reloadChatterData() {
  if (!roleData.value) return;
  
  const currentRoleId = roleData.value.id;
  
  roleChatterData.value = [];
  totalRoleChatterData.value = 0;
  if (loadedPages.value[currentRoleId]) {
    loadedPages.value[currentRoleId].clear();
  }
  
  await getChatterData();
}

async function getChatterData() {
  if (!roleData.value) return;
  
  const currentRoleId = roleData.value.id;
  const currentLength = roleChatterData.value.length;
  const page = Math.floor(currentLength / 10) + 1;
  
  if (!loadedPages.value[currentRoleId]) {
    loadedPages.value[currentRoleId] = new Set();
  }
  
  if (loadedPages.value[currentRoleId].has(page)) {
    return;
  }
  
  try {
    const { data } = await getChatterList(accountId.value, currentRoleId, "role", page);
    loadedPages.value[currentRoleId].add(page);
    
    if (data && data.count) {
      totalRoleChatterData.value = data.count;
    }
    
    if (data && data.results) {
      let chatterItems = data.results.map((el) => {
        const date = new Date(el.recorded_on);
        const milliseconds = date.getTime();
        const millisecondsString = milliseconds.toString();
        let nameParts = el.recorded_by_name.split(" ");
        const newEl = {
          id: el.id,
          IsExIQtiveBot: false,
          message: el.note,
          created_on: millisecondsString,
          IsHistory: el.is_history,
        };
        
        if (nameParts.length === 2) {
          newEl.avatar = {
            picture: "",
            firstName: nameParts[0],
            lastName: nameParts[1],
          };
        } else {
          newEl.avatar = {
            picture: "",
            firstName: el.recorded_by_name,
            lastName: "",
          };
        }
        
        return newEl;
      });
      roleChatterData.value.push(...chatterItems);
    }
  } catch (error) {
    console.error('Error fetching chatter data:', error);
  }
}

async function getRoleById(idRole) {
  try {
    const { data } = await getRole(accountId.value, idRole);
    roleData.value = data;
    if (showContextTabV2.value) {
      void loadContextFiles(idRole);
    }
  } catch (error) {
    console.error('Error fetching role:', error);
  }
}

function onActivateRoleConfirm() {
  const draftCount = draftResponsibilityCount.value;
  activateRoleDialog.value = false;
  if (draftCount > 0) {
    showActivateResponsibility.value = true;
    return;
  }
  processRoleActivation(false);
}

async function processRoleActivation(activateResponsibilities = false) {
  try {
    proxy.$loader?.setAppLoading(true);
    if (activateResponsibilities) {
      activateAttachedButtonLoader.value = true;
    } else {
      skipActivateButtonLoader.value = true;
    }
    const idRole = roleData.value?.id;
    const payload = {
      account_id: accountId.value,
      role_id: idRole,
      ...(activateResponsibilities && { activate_responsibilities: true }),
    };
    await updateActiveRole(accountId.value, idRole, payload);
    showActivateResponsibility.value = false;
    await getRoleById(idRole);
    await responsibilitiesTabRef.value?.fetchResponsibilities();
    await attachedToPositionsTabRef.value?.refresh();
    emit('roleDataUpdated');
  } catch (error) {
    console.error('Error activating role:', error);
  } finally {
    proxy.$loader?.setAppLoading(false);
    skipActivateButtonLoader.value = false;
    activateAttachedButtonLoader.value = false;
  }
}

function skipActivateResponsibility() {
  processRoleActivation(false);
}

function activateAttachedResponsibilities() {
  processRoleActivation(true);
}

async function onRoleRenamed(updatedRole) {
  try {
    proxy.$loader?.setAppLoading(true);
    showRenameRoleDialog.value = false;
    await getRoleById(updatedRole?.id || roleData.value?.id);
    emit('roleDataUpdated');
  } catch (error) {
    console.error('Error refreshing role after rename:', error);
  } finally {
    proxy.$loader?.setAppLoading(false);
  }
}

async function deleteOrgRole(deleteDraftResponsibilities) {
  try {
    proxy.$loader?.setAppLoading(true);
    const idRole = roleData.value?.id;
    const payload = {
      delete_draft_responsibilities: deleteDraftResponsibilities,
    };
    if (deleteDraftResponsibilities) {
      await deleteReadinessRoleWithResponsibilities(accountId.value, idRole, payload);
    } else {
      await deleteReadinessRole(accountId.value, idRole);
    }
    deleteRoleDialog.value = false;

    if (effectiveDrilldownInPanel.value) {
      emit('item-deleted', { type: 'role', id: idRole });
    } else if (proxy?.$router) {
      proxy.$notifications?.success?.('Role successfully deleted');
      // Navigate back to appropriate list based on route context
      const routeName = route?.name;
      if (routeName === 'RoleDetailFromPosition' && route?.params?.positionId) {
        // If came from position, go back to position detail
        proxy.$router.push({
          name: 'PositionDetailChild',
          params: { positionId: route.params.positionId },
          query: { positionTab: 'attached-roles' }
        }).catch(() => {});
      } else {
        // Otherwise go back to roles list
        proxy.$router.push('/organization/roles').catch(() => {});
      }
    }
  } catch (error) {
    console.error('Error deleting role:', error);
  } finally {
    proxy.$loader?.setAppLoading(false);
  }
}

async function retiredOrgRole() {
  try {
    proxy.$loader?.setAppLoading(true);
    const idRole = roleData.value?.id;
    const nameRole = roleData.value?.name;
    const payload = {
      name: nameRole,
      status: 'retired',
      account: accountId.value,
    };
    await updateRetiredRole(accountId.value, idRole, payload);
    retiredRoleDialog.value = false;
    await getRoleById(idRole);
    await responsibilitiesTabRef.value?.fetchResponsibilities();
    await attachedToPositionsTabRef.value?.refresh();
    emit('roleDataUpdated');
  } catch (error) {
    console.error('Error retiring role:', error);
  } finally {
    proxy.$loader?.setAppLoading(false);
  }
}

async function deActivateOrgRole() {
  try {
    proxy.$loader?.setAppLoading(true);
    const idRole = roleData.value?.id;
    const nameRole = roleData.value?.name;
    const payload = {
      name: nameRole,
      status: 'inactive',
      account: accountId.value,
    };
    await updateDeActiveRole(accountId.value, idRole, payload);
    deActivateRoleDialog.value = false;
    await getRoleById(idRole);
    await responsibilitiesTabRef.value?.fetchResponsibilities();
    await attachedToPositionsTabRef.value?.refresh();
    emit('roleDataUpdated');
  } catch (error) {
    console.error('Error deactivating role:', error);
  } finally {
    proxy.$loader?.setAppLoading(false);
  }
}

async function reActivateOrgRole() {
  try {
    proxy.$loader?.setAppLoading(true);
    const idRole = roleData.value?.id;
    const nameRole = roleData.value?.name;
    const payload = {
      name: nameRole,
      status: 'active',
      account: accountId.value,
    };
    await updateReActiveRole(accountId.value, idRole, payload);
    reActivateRoleDialog.value = false;
    await getRoleById(idRole);
    await responsibilitiesTabRef.value?.fetchResponsibilities();
    await attachedToPositionsTabRef.value?.refresh();
    emit('roleDataUpdated');
  } catch (error) {
    console.error('Error reactivating role:', error);
  } finally {
    proxy.$loader?.setAppLoading(false);
  }
}

async function refreshAfterArchiveChange() {
  showArchiveDialog.value = false;
  archiveAction.value = null;
  await responsibilitiesTabRef.value?.fetchResponsibilities();
  await attachedToPositionsTabRef.value?.refresh();
  emit('roleDataUpdated');
}

const unarchiveRole = useUnarchiveOrgObject('role', {
  getAccountId: () => accountId.value,
  openConfirm: () => {
    showArchiveDialog.value = true;
  },
  closeConfirm: () => {
    showArchiveDialog.value = false;
  },
  onUnarchived: async ({ id }) => {
    try {
      await getRoleById(id);
      await refreshAfterArchiveChange();
    } catch (error) {
      console.error('Error refreshing role after unarchive:', error);
    }
  },
  setLoading: (value) => proxy.$loader?.setAppLoading(value),
});
const {
  target: unarchiveTarget,
  conflictOpen: unarchiveConflictOpen,
  busy: unarchiveBusy,
  errorMessage: unarchiveError,
  nameError: unarchiveNameError,
} = unarchiveRole;

function onArchiveDialogClose() {
  showArchiveDialog.value = false;
  unarchiveRole.close();
}

async function onArchiveCatalogs() {
  if (archiveAction.value === 'unarchive') {
    await unarchiveRole.confirm();
    return;
  }
  try {
    proxy.$loader?.setAppLoading(true);
    const idRole = roleData.value?.id;
    await updateArchiveRole(accountId.value, 'role', idRole);
    await getRoleById(idRole);
    await refreshAfterArchiveChange();
  } catch (error) {
    console.error('Error archiving/unarchiving role:', error);
  } finally {
    proxy.$loader?.setAppLoading(false);
  }
}

async function handleOpenAttachResponsibility() {
  attachResponsibilityDialog.value = true;
  openDefault.value = true;
  responsibilitiesPage.value = 1;
  responsibilitiesSearchTerm.value = '';
  await fetchAttachableResponsibilitiesList();
}

async function fetchAttachableResponsibilitiesList(isLoadMore = false) {
  try {
    if (isLoadMore) {
      isLoadingMoreResponsibilities.value = true;
    }
    
    const offset = (responsibilitiesPage.value - 1) * responsibilitiesLimit;
    const response = await fetchAttachableResponsibilities(
      accountId.value,
      roleId.value,
      responsibilitiesLimit,
      offset,
      responsibilitiesSearchTerm.value
    );
    
    const newItems = (response.data.results || []).map((item) => ({
      id: item.id,
      code: item.code_str.match(/\d+/)[0],
      name: item.name,
      status: item.status,
      attached: item?.attached_to_role
    }));
    
    if (isLoadMore) {
      attachableResponsibilities.value.push(...newItems);
    } else {
      attachableResponsibilities.value = newItems;
    }
    
    hasMoreResponsibilitiesData.value = !!response.data.next;
  } catch (error) {
    console.error('Error fetching attachable responsibilities:', error);
    attachableResponsibilities.value = [];
    hasMoreResponsibilitiesData.value = false;
  } finally {
    if (isLoadMore) {
      isLoadingMoreResponsibilities.value = false;
    }
  }
}

function handleResponsibilitySelected(responsibility) {
  if (!responsibility || !responsibility.id) return;
  selectedResponsibilityId.value = responsibility.id;
}

async function handleAddNewResponsibility(responsibilityData) {
  try {
    proxy.$loader?.setAppLoading(true);
    const newResponsibility = await proxy.$store?.dispatch(
      'readinessCatalogsStore/createResponsibility',
      { name: responsibilityData.name, accountId: accountId.value }
    );
    
    if (newResponsibility && newResponsibility.data && newResponsibility.data.id) {
      selectedResponsibilityId.value = newResponsibility.data.id;
      await handleAttachResponsibility();
    }
  } catch (error) {
    console.error('Error creating and attaching responsibility:', error);
  } finally {
    proxy.$loader?.setAppLoading(false);
  }
}

async function handleAttachResponsibility() {
  if (!selectedResponsibilityId.value) return;
  
  try {
    isAssigningResponsibility.value = true;
    proxy.$loader?.setAppLoading(true);
    
    const payload = {
      attachment_id: selectedResponsibilityId.value,
    };
    
    await attachResponsibilityToRole(accountId.value, roleId.value, payload);
    
    attachResponsibilityDialog.value = false;
    selectedResponsibilityId.value = null;
    responsibilitiesPage.value = 1;
    responsibilitiesSearchTerm.value = '';
    
    await getRoleById(roleId.value);
    await responsibilitiesTabRef.value?.fetchResponsibilities();
    await attachedToPositionsTabRef.value?.refresh();
    emit('roleDataUpdated');
  } catch (error) {
    console.error('Error attaching responsibility:', error);
  } finally {
    isAssigningResponsibility.value = false;
    proxy.$loader?.setAppLoading(false);
  }
}

// AddAndAttachResponsibilityDialog already created and attached the Responsibility; just refresh.
async function handleResponsibilityCreatedAndAttached() {
  attachResponsibilityDialog.value = false;
  selectedResponsibilityId.value = null;
  responsibilitiesPage.value = 1;
  responsibilitiesSearchTerm.value = '';

  await getRoleById(roleId.value);
  await responsibilitiesTabRef.value?.fetchResponsibilities();
  await attachedToPositionsTabRef.value?.refresh();
  emit('roleDataUpdated');
}

async function loadMoreResponsibilities() {
  responsibilitiesPage.value += 1;
  await fetchAttachableResponsibilitiesList(true);
}

async function searchResponsibilities(searchTerm) {
  responsibilitiesSearchTerm.value = searchTerm;
  responsibilitiesPage.value = 1;
  await fetchAttachableResponsibilitiesList(false);
}

async function handleRoleDataUpdated() {
  if (roleId.value) {
    await getRoleById(roleId.value);
  }
  emit('roleDataUpdated');
}

async function handleDetachPosition() {
  await handleRoleDataUpdated();
}

async function handleMenuAction() {
  await handleRoleDataUpdated();
}

function handleOpenChatter() {
  // Chatter drawer is opened inside AttachedToPositionsTab/Table
}

function handleDrilldown(positionData) {
  if (!positionData?.id || !proxy?.$router) return;
  proxy.$router.push({
    name: 'PositionDetailChild',
    params: { positionId: positionData.id },
    query: { positionTab: 'attached-roles' },
  }).catch(() => {});
}

// Same as PositionForm: load GET .../account/:accountId/role/:roleId and refresh child tabs inside each action handler (no watch)

onMounted(async () => {
  try {
    accountId.value = JSON.parse(localStorage.getItem("currentAccountID"));
    roleId.value = computedRoleId.value;
    showContextTabV2.value =
      (await launchDarklyService.getFlag(LD_FLAG_IDEA_MDP_494, false)) === true;
    showContextCountBadge.value =
      (await launchDarklyService.getFlag(LD_FLAG_IDEA_MDP_523, false)) === true;
    contextIQOverlayEnabled.value = showContextCountBadge.value;
    skeletonLoadingEnabled.value =
      (await launchDarklyService.getFlag(LD_FLAG_IDEA_MDP_560, false)) === true;

    // ER-11574: keep the legacy full-screen spinner only when the skeleton rollout is off,
    // so the in-content skeleton is actually visible when idea-MDP-560 is on.
    if (!skeletonLoadingEnabled.value) {
      proxy.$loader?.show();
    }

    const roleTabFromQuery = route?.query?.roleTab;
    if (roleTabFromQuery) {
      const q = roleTabFromQuery.toLowerCase();
      const roleTabMap = {
        attached_responsibilities: 0,
        responsibilities: 0,
        attached_to_positions: 1,
        assignments: 2,
        purpose: 3,
        context: 3
      };
      const tabIndex = roleTabMap[q];
      if (tabIndex !== undefined) {
        selectedTab.value = tabIndex;
      }
    }
    
    await fetchRoleData();
  } finally {
    proxy.$loader?.hide();
  }
});
</script>

<style scoped>
.role-card {
  background: #fff;
  border-radius: 8px;
  box-shadow: 0px 1px 2px 0px #1018280F, 0px 1px 3px 0px #1018281A;
  border: 1px solid #EAECF0;
  width: 100%;
}
.role-header {
  padding: 10px 0px 0px 0px;
}
.breadcrumbs-area {
  padding: 10px 0px 0px 5px !important;
}
.role-card-main-content {
  border-radius: 0px 0px 7px 7px;
}
.role-skeleton-loader {
  padding: 16px;
}
::v-deep .role-header .header-header-container {
  position: relative;
  left: -10px;
}
::v-deep .role-header .header-org-menu {
  right: 0px !important;
}

::v-deep .filter-input-search {
  min-width: auto !important;
}

::v-deep .search-button {
  right: -22px !important;
}

::v-deep .search-bar-wrapper {
  align-items: center !important;
}

::v-deep .header-chatter-icon {
  margin-right: 7px !important;
}

::v-deep .header-org-menu {
  margin-right: 25px !important;
}

::v-deep .purpose-view {
  padding: 100%;
}

::v-deep .role-responsibilities-tab {
  box-shadow: none;
}

::v-deep .attached-to-positions-tab-container .attached-positions-table-wrapper {
  background: #fff;
  border: none;
  box-shadow: none;
  border-radius: 0px;
}
</style>
